import React, { useEffect, useState, useRef } from 'react';
import Macy from 'macy';
// You may need to install papaparse: npm install papaparse
import Papa from 'papaparse';
import { formatGalleryMetadata, deriveCategory } from '../hooks/useGalleryData';
import { GalleryItem } from '../types/gallery';
import { GALLERY_BREAKPOINTS } from '../config/galleryLayout';
const METADATA_URL = '/data/paintings/metadata_clean.csv';
const IMAGE_BASE_URL = '/data/paintings/images/';
const IMAGE_EXTS = ['.jpg', '.JPG', '.jpeg', '.png'];
const PLACEHOLDER_IMAGE = `${IMAGE_BASE_URL}landscape_garden4.JPG`;

let cachedItems: GalleryItem[] | null = null;
let cachedError: string | null = null;
let cachedCategories: string[] | null = null;
let metadataFetchPromise: Promise<GalleryItem[]> | null = null;

const STORAGE_KEY = 'galleryMetadataV1';
const STORAGE_CATEGORIES_KEY = 'galleryCategoriesV1';
const IS_DEV = process.env.NODE_ENV === 'development';

function computeCategories(rows: GalleryItem[]) {
    const catSet = new Set<string>();
    rows.forEach(r => {
        const c = deriveCategory(r);
        // Expose all items (including "other") in the gallery,
        // but don't surface "other" as a selectable filter.
        if (c && c.toLowerCase() !== 'other') catSet.add(c);
    });
    return Array.from(catSet).sort((a, b) => a.localeCompare(b));
}
// const [screenSize, setScreenSize] = useState(window.innerWidth);

// const nColumns = screenSize >= GALLERY_BREAKPOINTS.LARGE
//     ? GALLERY_COLUMNS.LARGE
//     : screenSize >= GALLERY_BREAKPOINTS.MEDIUM
//         ? GALLERY_COLUMNS.MEDIUM
//         : GALLERY_COLUMNS.SMALL;

// useEffect(() => {
//     const handleResize = () => setScreenSize(window.innerWidth);
//     window.addEventListener('resize', handleResize);
//     return () => window.removeEventListener('resize', handleResize);
// }, []);

/**
 * Props for the Gallery component.
 *
 * Provides configuration and callbacks used to render and interact with a collection of gallery items.
 *
 * @property className - Optional CSS class name(s) to apply to the gallery root element.
 * @property showMetadata - Optional flag to indicate whether item metadata (e.g. title, date, caption) should be displayed alongside each item.
 * @property onItemClick - Optional callback invoked when an item in the gallery is clicked. Receives the clicked GalleryItem and the HTMLElement that was interacted with.
 * @property categoryFilter - Optional category to filter the displayed items by. Examples: 'landscape', 'still life', 'portrait', 'markets & crowds', 'people', or 'all'.
 * @property onDataLoaded - Optional callback invoked after the gallery's data is loaded or refreshed. Receives the array of loaded GalleryItem objects and the computed list of available category strings.
 *
 * @remarks
 * All properties are optional to allow flexible usage patterns (controlled, partially controlled, or fully internal).
 */

type GalleryProps = {
    className?: string;
    showMetadata?: boolean;
    onItemClick?: (item: GalleryItem, el: HTMLElement) => void;
    categoryFilter?: string; // e.g., 'landscape', 'still life', 'portrait', 'markets & crowds', 'people', 'plants', or 'all'
    onDataLoaded?: (items: GalleryItem[], categories: string[]) => void;
    useMacy?: boolean; // enable Macy.js layout (row-major masonry)
};


const Gallery: React.FC<GalleryProps> = ({ className, showMetadata = true, onItemClick, categoryFilter, onDataLoaded, useMacy = true }) => {
    const [items, setItems] = useState<GalleryItem[]>(() => cachedItems ?? []);
    // Column count now driven purely by CSS media queries; Macy breakpoints handle JS side.
    const [loading, setLoading] = useState(!cachedItems && !cachedError);
    const [error, setError] = useState<string | null>(cachedError);
    const rootRef = useRef<HTMLDivElement | null>(null);
    const macyRef = useRef<Macy | null>(null);

    // Fetch metadata once on mount (with module-level + sessionStorage cache so full reloads/incognito navigations accelerate)
    useEffect(() => {
        // Try sessionStorage priming (parsing is fast vs network)
        // Skip loading from cache in development
        if (!IS_DEV && !cachedItems) {
            try {
                const raw = sessionStorage.getItem(STORAGE_KEY);
                if (raw) {
                    const parsed: GalleryItem[] = JSON.parse(raw);
                    if (Array.isArray(parsed) && parsed.length) {
                        cachedItems = parsed;
                        const catRaw = sessionStorage.getItem(STORAGE_CATEGORIES_KEY);
                        if (catRaw) {
                            const catParsed = JSON.parse(catRaw);
                            if (Array.isArray(catParsed)) cachedCategories = catParsed as string[];
                        } else {
                            cachedCategories = computeCategories(parsed);
                        }
                    }
                }
            } catch { /* ignore corrupted cache */ }
        }
        if (cachedItems) {
            setItems(cachedItems);
            setError(null);
            setLoading(false);
            return;
        }

        if (cachedError) {
            setError(cachedError);
            setLoading(false);
            return;
        }

        let cancelled = false;

        if (!metadataFetchPromise) {
            metadataFetchPromise = fetch(METADATA_URL)
                .then((response) => {
                    if (!response.ok) throw new Error('Failed to load metadata');
                    return response.text();
                })
                .then((csvText) => {
                    const parsed = Papa.parse(csvText, { header: true });
                    const rows: GalleryItem[] = (parsed.data as any[]).filter(r => r && (r.ID || r.File));
                    cachedItems = rows;
                    cachedCategories = computeCategories(rows);
                    cachedError = null;
                    // Persist for future reloads
                    try {
                        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(rows));
                        sessionStorage.setItem(STORAGE_CATEGORIES_KEY, JSON.stringify(cachedCategories));
                    } catch { /* storage quota or disabled */ }
                    return rows;
                })
                .catch((err) => {
                    cachedError = err.message;
                    throw err;
                })
                .finally(() => {
                    metadataFetchPromise = null;
                });
        }

        metadataFetchPromise
            ?.then((rows) => {
                if (cancelled) return;
                setItems(rows);
                setError(null);
                setLoading(false);
            })
            .catch((err) => {
                if (cancelled) return;
                setError(err.message);
                setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, []);

    // Report categories when items load or when onDataLoaded changes
    useEffect(() => {
        if (!onDataLoaded) return;
        const cats = items.length ? (cachedCategories ?? computeCategories(items)) : [];
        onDataLoaded(items, cats);
    }, [items, onDataLoaded]);

    // Removed dynamic JS column sizing; CSS + Macy breakpoints will handle responsiveness.

    // Macy.js integration: single instance, re-init on data/filter/column changes
    useEffect(() => {
        if (!useMacy) return;
        const el = rootRef.current;
        if (!el) return;
        if (!macyRef.current) {
            if (!items.length) return;
            macyRef.current = new Macy({
                container: el,
                columns: 5,
                trueOrder: true,
                waitForImages: false,
                margin: { x: 22, y: 22 },
                breakAt: {
                    [GALLERY_BREAKPOINTS.LARGE]: 5,
                    [GALLERY_BREAKPOINTS.MEDIUM]: 3,
                    [GALLERY_BREAKPOINTS.SMALL]: 2
                }
            });
            // Single layout pass based on metadata-driven wrapper heights
            macyRef.current.reInit();
            // Listen for resize
            const handleResize = () => { if (macyRef.current) macyRef.current.reInit(); };
            window.addEventListener('resize', handleResize);
            // Cleanup only on unmount
            return () => {
                window.removeEventListener('resize', handleResize);
                if (macyRef.current) {
                    macyRef.current.remove();
                    macyRef.current = null;
                }
            };
        } else {
            // Existing instance: re-init after DOM changes (filter / items / columns)
            macyRef.current.reInit();
        }
    }, [useMacy, items, categoryFilter]);

    if (loading) return;
    if (error) return <div>Error: {error}</div>;

    // Apply category filter:
    // When a specific category is selected (not 'all'), show all items of that category regardless of the 'On Main Page' flag.
    // When 'all' (or no category) is active, only show items whose 'On Main Page' column is truthy ('True').
    const normalizedFilter = categoryFilter ? categoryFilter.toLowerCase() : 'all';
    const filteredItems = (normalizedFilter !== 'all')
        ? items.filter(it => {
            const cat = deriveCategory(it);
            return cat && String(cat).toLowerCase() === normalizedFilter;
        })
        : items.filter(it => {
            const raw = (it as any)['On Main Page'];
            // Default to true if missing to preserve legacy items without the column.
            if (raw === undefined || raw === null || raw === '') return true;
            return String(raw).trim().toLowerCase() === 'true';
        });

    // Sort by OrderBy (if present) but rely solely on responsive CSS grid (no explicit-grid override)
    const renderItems = [...filteredItems].sort((a, b) => {
        const aOrdRaw = (a as any)['OrderBy'];
        const bOrdRaw = (b as any)['OrderBy'];
        const aOrd = aOrdRaw !== undefined && aOrdRaw !== '' && !isNaN(Number(aOrdRaw)) ? Number(aOrdRaw) : 0;
        const bOrd = bOrdRaw !== undefined && bOrdRaw !== '' && !isNaN(Number(bOrdRaw)) ? Number(bOrdRaw) : 0;
        return aOrd - bOrd;
    });


    return (
        <div ref={rootRef} className={`gallery-grid${useMacy ? ' macy-enabled' : ''}${className ? ' ' + className : ''}`}>
            {renderItems.map((item, idx) => {
                // Format: "{Name} ({Year}), {Medium} on {Material}, {Width (cm)} cm x {Height (cm)} cm"
                const formattedText = formatGalleryMetadata(item);
                // Build candidate image URLs: prefer File if provided; else try ID plus fallbacks
                const fileVal = (item.File && String(item.File).trim()) || '';
                const idVal = (item.ID && String(item.ID).trim()) || '';
                const bases: string[] = [];
                if (fileVal) {
                    bases.push(fileVal);
                    // If file provided without an extension, also try with known extensions
                    if (!IMAGE_EXTS.some(ext => fileVal.toLowerCase().endsWith(ext.toLowerCase()))) {
                        IMAGE_EXTS.forEach(ext => bases.push(`${fileVal}${ext}`));
                    }
                }
                if (idVal) {
                    const variants = new Set<string>();
                    const addVar = (s: string) => { if (s) variants.add(s); };
                    addVar(idVal);
                    Array.from(variants).forEach(v => {
                        if (IMAGE_EXTS.some(ext => v.toLowerCase().endsWith(ext.toLowerCase()))) {
                            bases.push(v);
                        } else {
                            IMAGE_EXTS.forEach(ext => bases.push(`${v}${ext}`));
                        }
                    });
                }
                // Prefix base URL and dedupe
                const attemptUrls = Array.from(new Set(bases.map(b => `${IMAGE_BASE_URL}${b}`)));
                const fullResSrc = attemptUrls[0] || '';
                const thumbSrc = fullResSrc;
                // Responsive layout only: no explicit grid placement computed here.
                const gridPlacementStyle: React.CSSProperties = {};

                // Thumbnail cropping: enforce aspect ratio bounds.
                // If too wide (> MAX_AR) crop horizontally; if too tall (< MIN_AR) crop vertically.
                const MAX_AR = 1.58; // upper bound (landscape too wide)
                const MIN_AR = 0.65; // lower bound (portrait too tall)

                const arMetaRaw = (item as any)['AspectRatio'];
                const arMeta =
                    arMetaRaw !== undefined &&
                        arMetaRaw !== '' &&
                        !isNaN(Number(arMetaRaw))
                        ? Number(arMetaRaw)
                        : undefined;

                const wRaw = (item as any)['Width (cm)'];
                const hRaw = (item as any)['Height (cm)'];
                const wNum =
                    wRaw !== undefined &&
                        wRaw !== '' &&
                        !isNaN(Number(wRaw))
                        ? Number(wRaw)
                        : undefined;
                const hNum =
                    hRaw !== undefined &&
                        hRaw !== '' &&
                        !isNaN(Number(hRaw))
                        ? Number(hRaw)
                        : undefined;

                const dimsRatio = wNum && hNum ? wNum / hNum : undefined;
                const effectiveRatio = arMeta ?? dimsRatio;

                // boxRatio defines the card height and is clamped
                // into [MIN_AR, MAX_AR] so all thumbs respect bounds.
                let boxRatio: number | null = null;
                if (effectiveRatio !== undefined) {
                    boxRatio = Math.min(MAX_AR, Math.max(MIN_AR, effectiveRatio));
                }

                // We still treat anything outside the range as "cropped".
                const usesCrop =
                    effectiveRatio !== undefined &&
                    (effectiveRatio > MAX_AR || effectiveRatio < MIN_AR);

                const cropWrapperStyle: React.CSSProperties = boxRatio !== null ? {
                    position: 'relative',
                    width: '100%',
                    paddingTop: `${100 / boxRatio}%`,
                    overflow: 'hidden',
                    borderRadius: '12px'
                } : {
                    position: 'relative',
                    width: '100%',
                    borderRadius: '12px'
                };

                const imgStyle: React.CSSProperties = usesCrop ? {
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: '50% 50%',
                    display: 'block'
                } : {
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    display: 'block'
                };
                const itemKey = (item.ID && String(item.ID)) || (item.File && String(item.File)) || String(idx);

                return (
                    <div
                        className="gallery-item"
                        key={itemKey}
                        data-category={deriveCategory(item)}
                        data-title={item['Name'] || ''}
                        data-name={item['Name'] || ''}
                        data-year={item['Year'] || ''}
                        data-medium={`${item['Medium'] || ''}${item['Material'] ? ` on ${item['Material']}` : ''}`}
                        data-size={`${item['Width (cm)'] || ''}${item['Width (cm)'] ? ' cm' : ''}${item['Height (cm)'] ? ' x ' : ''}${item['Height (cm)'] || ''}${item['Height (cm)'] ? ' cm' : ''}`}
                        data-img={fullResSrc}
                        onClick={(e) => onItemClick && onItemClick(item, e.currentTarget as HTMLElement)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' && onItemClick) onItemClick(item, e.currentTarget as HTMLElement); }}
                        style={{
                            ...gridPlacementStyle,
                            // gentle stagger so items don't pop in all at once
                            animationDelay: `${idx * 66}ms`
                        }}
                    >
                        <div className={usesCrop ? 'thumb-crop cropped' : 'thumb-crop'} style={cropWrapperStyle}>
                            <img
                                src={thumbSrc}
                                alt={(item['Name'] as string) || (item.ID as string) || ''}
                                className="gallery-image"
                                loading={idx < 12 ? 'eager' : 'lazy'}
                                fetchPriority={idx < 12 ? 'high' : 'auto'}
                                decoding="async"
                                style={imgStyle}
                                data-alt-srcs={attemptUrls.slice(1).join('|')}
                                onError={(e) => {
                                    const img = e.currentTarget as HTMLImageElement;
                                    const alt = img.dataset.altSrcs || '';
                                    const parts = alt.split('|').filter(Boolean);

                                    // Try candidates off-DOM and only set img.src when a candidate actually loads.
                                    // This avoids visible re-request flicker and repeated reloads of the displayed img.
                                    const tryNext = (candidates: string[]) => {
                                        if (!candidates || candidates.length === 0) {
                                            // no candidates left -> show placeholder (only once)
                                            if (!img.dataset.placeholder) {
                                                img.dataset.placeholder = '1';
                                                img.src = PLACEHOLDER_IMAGE;
                                                const parent = img.closest('.gallery-item') as HTMLElement | null;
                                                if (parent) parent.setAttribute('data-img', PLACEHOLDER_IMAGE);
                                            }
                                            return;
                                        }
                                        const candidate = candidates.shift() as string;
                                        // Preload the candidate
                                        const tester = new Image();
                                        tester.onload = () => {
                                            try {
                                                // Only update the visible img once the preload succeeded
                                                img.src = candidate;
                                                const parent = img.closest('.gallery-item') as HTMLElement | null;
                                                if (parent) parent.setAttribute('data-img', candidate);
                                                img.dataset.altSrcs = candidates.join('|');
                                            } catch {
                                                // ignore assignment errors
                                            }
                                        };
                                        tester.onerror = () => {
                                            // try the next candidate
                                            tryNext(candidates);
                                        };
                                        // start preload
                                        tester.src = candidate;
                                    };

                                    tryNext(parts);
                                }}
                            />
                        </div>
                        {/* Title overlay for hover (appears bottom-left on Art page) */}
                        {item['Name'] && (
                            <div className="title-overlay">
                                {String(item['Name'])}
                            </div>
                        )}
                        {showMetadata && (
                            <div className="gallery-metadata">
                                {formattedText}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default Gallery;
